'use client';

import * as React from 'react';

import { Upload } from 'lucide-react';

import { toast } from '@/hooks/use-toast';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { Navbar } from '@/components/shared/navbar/navbar';

import { useDispatch } from '@/lib/redux/useDispatch';
import { UploadService } from '@/lib/services/upload';
import { actions } from '@/redux/actions';

const fetchNotifier = (fn: () => Promise<{ type: string }>) => async () => {
  const resp = await fn();
  if (resp.type.includes('fulfilled')) {
    toast({
      title: 'Success',
      description: 'Successfully fetched user data.',
      variant: 'default',
    });
  }
};

export function Dashboard() {
  const dispatch = useDispatch();
  const [file, setFile] = React.useState<File | null>(null);
  const [isUploading, setIsUploading] = React.useState(false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setFile(event.target.files[0]);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    console.log('handle submit called');
    event.preventDefault();
    if (!file) {
      toast({
        title: 'No file selected',
        description: 'Please select an audio file to upload.',
        variant: 'destructive',
      });
      return;
    }

    setIsUploading(true);
    try {
      await UploadService.uploadFile(file);
      toast({
        title: 'File uploaded successfully',
        description: `${file.name} has been uploaded.`,
      });
      setFile(null);
      // Reset the file input
      const fileInput = document.getElementById('audio-file') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch (error) {
      console.error('Upload failed:', error);
      toast({
        title: 'Upload failed',
        description: 'There was an error uploading your file. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full flex-col">
      <Navbar
        links={[
          { to: '/', label: 'Dashboard' },
          { to: '/sessions', label: 'Sessions' },
        ]}
      />

      <div className="grid lg:grid-cols-5 h-full">
        <div className="pb-12 hidden lg:block">
          <div className="space-y-4 py-4">
            <div className="px-3 py-2">
              <h2 className='mb-2 px-4 text-lg font-semibold tracking-tight'>Discover</h2>
              <div className="space-y-1">
                <button className='inline-flex items-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80 h-9 px-4 py-2 w-full justify-start'>
                  Sessions
                </button>
                <button className='inline-flex items-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-accent hover:text-accent-foreground h-9 px-4 py-2 w-full justify-start'>
                  Settings
                </button>
              </div>
            </div>
          </div>

          <div className="space-y-4 py-4">

          </div>

          <div className="py-2">

          </div>
        </div>
        <div className="col-span-4 lg:col-span-4 lg:border-l">
          <div className="flex flex-1 flex-col gap-4 px-4 py-10">
            <div className="mx-auto w-full max-w-3xl rounded-xl bg-card p-6 shadow-sm">
              <h2 className="mb-4 text-2xl font-bold">Upload Audio File</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid w-full max-w-sm items-center gap-1.5">
                  <Label htmlFor="audio-file">Select Audio File</Label>
                  <Input
                    id="audio-file"
                    type="file"
                    accept="audio/*"
                    onChange={handleFileChange}
                    disabled={isUploading}
                  />
                </div>
                <Button type="submit" disabled={!file || isUploading}>
                  {isUploading ? (
                    <>
                      <Upload className="mr-2 h-4 w-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      Upload Audio
                    </>
                  )}
                </Button>
              </form>
              <Button onClick={fetchNotifier(() => dispatch(actions.getMe()))}>
                <code>getMe</code>
              </Button>
              <Button onClick={fetchNotifier(() => dispatch(actions.getAccounts()))}>
                <code>getAccounts</code>
              </Button>
            </div>
            <div className="mx-auto h-full w-full max-w-3xl rounded-xl bg-muted/50" />
          </div>
        </div>
      </div>

    </div>
  );
}
